import React from 'react';
import { Clock, Phone, Mail, MapPin, Navigation } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import OpenStatusBadge from './OpenStatusBadge';

const ContactSection = () => {
  return (
    <section id="contact" className="py-20 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Visit Us Today Header - Centered on Page */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Visit Us Today
          </h2>
          <div className="w-24 h-1 mx-auto mb-6" style={{ backgroundColor: '#00B3B3' }} />
          <OpenStatusBadge />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Side - Location, Store Hours and Contact */}
          <div className="space-y-8">
            {/* Location */}
            <Card className="shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(0, 179, 179, 0.1)' }}>
                    <MapPin className="w-6 h-6" style={{ color: '#00B3B3' }} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Location</h3>
                </div>
                <div>
                  <p className="text-gray-700 font-medium">2560 N Illinois 83</p>
                  <p className="text-gray-700 font-medium mb-4">Round Lake Beach, IL 60073</p>
                  <a
                    href="https://www.google.com/maps/dir/?api=1&destination=Nexera+Phone+%26+Tech+Repair+2560+N+Illinois+83+Round+Lake+Beach+IL+60073"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all duration-300 hover:scale-[1.03]"
                    style={{
                      backgroundColor: '#00B3B3',
                      textDecoration: 'none',
                      boxShadow: '0 4px 12px -4px rgba(0, 179, 179, 0.5)',
                    }}
                    data-testid="get-directions-btn"
                  >
                    <Navigation className="w-4 h-4" />
                    Get Directions
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Store Hours */}
            <Card className="shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(0, 179, 179, 0.1)' }}>
                    <Clock className="w-6 h-6" style={{ color: '#00B3B3' }} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Store Hours</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-700">Monday – Saturday</span>
                    <span className="font-medium" style={{ color: '#00B3B3' }}>10:00 AM - 6:00 PM</span>
                  </div>
                  <div className="h-px bg-gray-200" />
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-700">Sunday</span>
                    <span className="font-medium" style={{ color: '#00B3B3' }}>11:00 AM - 4:00 PM</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Card className="shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(0, 179, 179, 0.1)' }}>
                      <Phone className="w-5 h-5" style={{ color: '#00B3B3' }} />
                    </div>
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">Phone</h4>
                  <a href="tel:2244199922" className="text-gray-600 transition-colors" style={{ textDecoration: 'none' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#00B3B3'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#4b5563'}
                  >
                    (224) 419-9922
                  </a>
                </CardContent>
              </Card>

              <Card className="shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(0, 179, 179, 0.1)' }}>
                      <Mail className="w-5 h-5" style={{ color: '#00B3B3' }} />
                    </div>
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">Email</h4>
                  <a href="mailto:info@nexerarepair.com" className="text-gray-600 transition-colors" style={{ textDecoration: 'none' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#00B3B3'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#4b5563'}
                  >
                    info@nexerarepair.com
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Right Side - Interactive Map */}
          <div className="relative rounded-lg overflow-hidden shadow-lg" style={{ height: '600px' }}>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d11838.0!2d-88.0841!3d42.3733!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0xf4425fddd7e0d8e0!2sNexera%20Phone%20%26%20Tech%20Repair!5e0!3m2!1sen!2sus!4v1234567890!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Store Location"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;